/**
 * CurrencyTicker — live FX rates, zero typing.
 *
 * Smart pair selection (no input fields ever):
 *   - "Home currency" = derived from user's primary country (set once, on
 *     first install). Defaults to the country reported by IP/geolocation.
 *   - "Quote currencies" = a preset of 4 popular pairs adapted to the home
 *     currency: e.g. for MAD: USD, EUR, SAR, AED.
 *   - On country change (LocationService.countryCode flips), the widget
 *     auto-prepends the new country's local currency to the strip and
 *     pins itself to the top of the feed (handled by AnticipationEngine).
 *
 * Data source chain (free):
 *   1. /aweni/api/v1/fx?base=XXX  — backend proxy with ECB + Frankfurter
 *      free-tier as upstream. Cached 30 min.
 *   2. https://api.frankfurter.app/latest?from=XXX  — direct ECB feed.
 *
 * Output: a 4-line ticker showing 1 unit of base = X quote, with daily
 * change %. Tap a row → opens a mini converter (slider 1, 10, 100, 1000)
 * that performs offline arithmetic on the cached rate.
 */

import type { Widget, WidgetContext } from '../types';
import { swr } from '../../../services/swr';

interface FxPayload {
  base: string;
  rates: Record<string, number>;
  date: string;
  fetchedAt: number;
}

// Country → likely base currency code. Covers ~50 most relevant for AWENI.
const COUNTRY_CCY: Record<string, string> = {
  US: 'USD', GB: 'GBP', AU: 'AUD', CA: 'CAD', NZ: 'NZD',
  EU: 'EUR', FR: 'EUR', DE: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', BE: 'EUR', PT: 'EUR', IE: 'EUR', AT: 'EUR', GR: 'EUR', FI: 'EUR',
  CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON', BG: 'BGN',
  TR: 'TRY', RU: 'RUB',
  JP: 'JPY', KR: 'KRW', CN: 'CNY', TW: 'TWD', HK: 'HKD', SG: 'SGD', MY: 'MYR', TH: 'THB', ID: 'IDR', PH: 'PHP', VN: 'VND', IN: 'INR', PK: 'PKR', BD: 'BDT',
  SA: 'SAR', AE: 'AED', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR', JO: 'JOD', LB: 'LBP', IL: 'ILS',
  EG: 'EGP', MA: 'MAD', DZ: 'DZD', TN: 'TND', LY: 'LYD', SD: 'SDG',
  NG: 'NGN', KE: 'KES', GH: 'GHS', ZA: 'ZAR', ET: 'ETB',
  MX: 'MXN', BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', VE: 'VES',
};

// For a given home currency, what 4 quote currencies are most relevant.
const QUOTES_FOR: Record<string, string[]> = {
  USD: ['EUR', 'GBP', 'JPY', 'CAD'],
  EUR: ['USD', 'GBP', 'CHF', 'JPY'],
  GBP: ['EUR', 'USD', 'CHF', 'JPY'],
  MAD: ['USD', 'EUR', 'SAR', 'AED'],
  EGP: ['USD', 'EUR', 'SAR', 'AED'],
  DZD: ['USD', 'EUR', 'SAR', 'AED'],
  TND: ['USD', 'EUR', 'SAR', 'AED'],
  SAR: ['USD', 'EUR', 'AED', 'GBP'],
  AED: ['USD', 'EUR', 'SAR', 'GBP'],
  TRY: ['USD', 'EUR', 'GBP', 'JPY'],
  INR: ['USD', 'EUR', 'GBP', 'AED'],
  PKR: ['USD', 'EUR', 'GBP', 'SAR'],
  IDR: ['USD', 'EUR', 'SGD', 'JPY'],
  CNY: ['USD', 'EUR', 'JPY', 'HKD'],
  JPY: ['USD', 'EUR', 'CNY', 'KRW'],
};

const DEFAULT_QUOTES = ['USD', 'EUR', 'GBP', 'SAR'];

const ENDPOINTS = (base: string): string[] => [
  `/aweni/api/v1/fx?base=${encodeURIComponent(base)}`,
  `https://api.frankfurter.app/latest?from=${encodeURIComponent(base)}`,
];

async function fetchRates(base: string): Promise<FxPayload | null> {
  for (const url of ENDPOINTS(base)) {
    try {
      const resp = await fetch(url, { cache: 'no-store' });
      if (!resp.ok) continue;
      const data = await resp.json() as { base?: string; rates?: Record<string, number>; date?: string };
      if (data?.rates && Object.keys(data.rates).length > 0) {
        return {
          base: data.base || base,
          rates: data.rates,
          date: data.date || new Date().toISOString().slice(0, 10),
          fetchedAt: Date.now(),
        };
      }
    } catch { /* try next */ }
  }
  return null;
}

const CCY_FLAG: Record<string, string> = {
  USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧', JPY: '🇯🇵', CHF: '🇨🇭', CAD: '🇨🇦', AUD: '🇦🇺',
  CNY: '🇨🇳', HKD: '🇭🇰', SGD: '🇸🇬', INR: '🇮🇳', PKR: '🇵🇰', IDR: '🇮🇩', KRW: '🇰🇷',
  SAR: '🇸🇦', AED: '🇦🇪', QAR: '🇶🇦', KWD: '🇰🇼', BHD: '🇧🇭', OMR: '🇴🇲',
  MAD: '🇲🇦', EGP: '🇪🇬', DZD: '🇩🇿', TND: '🇹🇳',
  TRY: '🇹🇷', RUB: '🇷🇺', SEK: '🇸🇪', NOK: '🇳🇴', DKK: '🇩🇰', PLN: '🇵🇱',
  ZAR: '🇿🇦', NGN: '🇳🇬', KES: '🇰🇪',
  MXN: '🇲🇽', BRL: '🇧🇷', ARS: '🇦🇷',
};

function flagFor(ccy: string): string { return CCY_FLAG[ccy] || '💱'; }

function formatRate(rate: number): string {
  if (rate >= 100) return rate.toFixed(2);
  if (rate >= 10) return rate.toFixed(3);
  if (rate >= 1) return rate.toFixed(4);
  return rate.toFixed(5);
}

export const CurrencyTicker: Widget = {
  id: 'currency-ticker',
  label: {
    en: 'Currency',
    fr: 'Devises',
    ar: 'العملات',
    es: 'Divisas',
    de: 'Währungen',
  },
  icon: '💱',
  defaultVisible: true,
  defaultOrder: 15,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const loc = ctx.getLocationService();
    const homeCcy = (loc.countryCode && COUNTRY_CCY[loc.countryCode]) || 'USD';
    const quotes = QUOTES_FOR[homeCcy] || DEFAULT_QUOTES;

    container.innerHTML = `
      <div class="aweni-fx-card" style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">💱</span>
          <span style="font-size:13px;font-weight:700;color:var(--ln-text-primary,var(--text-primary,#1E293B));">${escapeHtml(t('widget_currency_title') || 'Currency')}</span>
          <span style="margin-inline-start:auto;font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">${flagFor(homeCcy)} 1 ${escapeHtml(homeCcy)}</span>
        </div>

        <div data-fx-list style="display:flex;flex-direction:column;gap:6px;">
          ${quotes.map((q) => `
            <button type="button" data-quote="${q}"
              style="display:flex;align-items:center;gap:10px;width:100%;padding:10px 8px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));border-radius:10px;background:transparent;cursor:pointer;color:inherit;font:inherit;text-align:start;min-height:48px;">
              <span style="font-size:18px;">${flagFor(q)}</span>
              <span style="font-weight:700;font-size:13px;">${escapeHtml(q)}</span>
              <span data-rate-cell="${q}" style="margin-inline-start:auto;font-variant-numeric:tabular-nums;font-weight:600;font-size:13px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">…</span>
            </button>
          `).join('')}
        </div>

        <div data-fx-status style="margin-top:8px;font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));"></div>

        <!-- Mini converter (revealed on row click) -->
        <div data-fx-converter style="display:none;margin-top:10px;padding:10px;border-radius:10px;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));"></div>
      </div>
    `;

    const list = container.querySelector('[data-fx-list]') as HTMLElement | null;
    const status = container.querySelector('[data-fx-status]') as HTMLElement | null;
    const conv = container.querySelector('[data-fx-converter]') as HTMLElement | null;

    let lastPayload: FxPayload | null = null;

    const renderRates = (payload: FxPayload): void => {
      lastPayload = payload;
      quotes.forEach((q) => {
        const cell = list?.querySelector(`[data-rate-cell="${q}"]`) as HTMLElement | null;
        if (!cell) return;
        const rate = payload.rates[q];
        cell.textContent = typeof rate === 'number' ? formatRate(rate) : '—';
      });
      if (status) {
        const dt = new Date(payload.fetchedAt);
        status.textContent = `${t('widget_currency_updated') || 'Updated'} ${dt.toLocaleTimeString(ctx.locale, { hour: '2-digit', minute: '2-digit' })}`;
      }
    };

    const renderConverter = (quote: string): void => {
      if (!conv || !lastPayload) return;
      const rate = lastPayload.rates[quote];
      if (typeof rate !== 'number') return;
      const presets = [1, 10, 100, 1000];
      conv.style.display = 'block';
      conv.innerHTML = `
        <div style="font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));text-transform:uppercase;letter-spacing:0.4px;margin-bottom:6px;">
          ${flagFor(homeCcy)} ${escapeHtml(homeCcy)} → ${flagFor(quote)} ${escapeHtml(quote)} @ ${formatRate(rate)}
        </div>
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:6px;">
          ${presets.map((p) => `
            <div style="display:flex;flex-direction:column;align-items:center;padding:8px 4px;border-radius:8px;background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,0.06));">
              <div style="font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">${p} ${escapeHtml(homeCcy)}</div>
              <div style="font-size:13px;font-weight:700;font-variant-numeric:tabular-nums;">${formatRate(p * rate)}</div>
            </div>
          `).join('')}
        </div>
      `;
    };

    list?.querySelectorAll<HTMLButtonElement>('[data-quote]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const q = btn.dataset.quote || '';
        if (q) renderConverter(q);
      });
    });

    // SWR fetch — 30 min fresh, 24 h stale-tolerant
    swr<FxPayload>(
      `fx_${homeCcy}`,
      () => fetchRates(homeCcy),
      {
        freshTtlMs: 30 * 60 * 1000,
        staleTtlMs: 24 * 60 * 60 * 1000,
        onUpdate: (data) => renderRates(data),
      },
    ).then((data) => {
      if (data) renderRates(data);
      else if (status) status.textContent = t('widget_currency_offline') || 'Rates unavailable. Will retry later.';
    }).catch(() => { /* silent */ });
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
